import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Building2,
  AlertTriangle,
  ExternalLink,
  MoreHorizontal,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { FundLookupDialog } from "@/components/funds/FundLookupDialog";
import { DuplicateResolver } from "@/components/funds/DuplicateResolver";
import { findPotentialDuplicates } from "@/lib/fund-matching";

interface Fund {
  id: string;
  name: string;
  issuer: string | null;
  type: string;
  vintage_year: number | null;
  sec_cik: string | null;
  lei: string | null;
  identifier_status: string | null;
  created_at: string;
  _positionCount?: number;
}

export default function FundCatalog() {
  const { orgId, isMFO } = useTenant();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [addFundOpen, setAddFundOpen] = useState(false);
  const [duplicateResolverOpen, setDuplicateResolverOpen] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);

  useEffect(() => {
    if (orgId) {
      fetchFunds();
      checkForDuplicates();
    }
  }, [orgId]);

  const fetchFunds = async () => {
    if (!orgId) return;

    try {
      setLoading(true);

      // Fetch funds (instruments) that are funds
      const { data, error } = await supabase
        .from("instruments")
        .select(
          `
          id,
          name,
          issuer,
          type,
          vintage_year,
          sec_cik,
          lei,
          identifier_status,
          created_at,
          positions(id)
        `
        )
        .eq("org_id", orgId)
        .in("type", ["private_fund", "public_fund"])
        .is("family_id", null) // Master catalog only (org-level)
        .order("name");

      if (error) throw error;

      // Map to include position count
      const fundsWithCounts =
        data?.map((f) => ({
          ...f,
          _positionCount: (f.positions as { id: string }[])?.length || 0,
        })) || [];

      setFunds(fundsWithCounts);
    } catch (err) {
      console.error("Failed to fetch funds:", err);
      toast.error("Failed to fetch fund catalog");
    } finally {
      setLoading(false);
    }
  };

  const checkForDuplicates = async () => {
    if (!orgId) return;

    try {
      const duplicates = await findPotentialDuplicates(orgId, 0.7);
      setDuplicateCount(duplicates.length);
    } catch (err) {
      console.error("Failed to check duplicates:", err);
    }
  };

  const handleDeleteFund = async (fund: Fund) => {
    if (fund._positionCount && fund._positionCount > 0) {
      toast.error(
        "Cannot delete fund with active positions. Remove positions first."
      );
      return;
    }

    if (!confirm(`Are you sure you want to delete "${fund.name}"?`)) {
      return;
    }

    try {
      // Delete aliases first
      await supabase
        .from("fund_name_aliases")
        .delete()
        .eq("instrument_id", fund.id);

      // Delete the fund
      const { error } = await supabase
        .from("instruments")
        .delete()
        .eq("id", fund.id);

      if (error) throw error;

      toast.success("Fund deleted");
      fetchFunds();
    } catch (err) {
      console.error("Failed to delete fund:", err);
      toast.error("Failed to delete fund");
    }
  };

  const handleVerifyIdentifiers = async (fund: Fund) => {
    try {
      const { error } = await supabase
        .from("instruments")
        .update({
          identifier_status: "verified",
          verified_at: new Date().toISOString(),
        })
        .eq("id", fund.id);

      if (error) throw error;

      toast.success("Identifiers marked as verified");
      fetchFunds();
    } catch (err) {
      console.error("Failed to verify:", err);
      toast.error("Failed to verify identifiers");
    }
  };

  const filteredFunds = funds.filter(
    (f) =>
      f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.issuer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.sec_cik?.includes(searchQuery) ||
      f.lei?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "verified":
        return (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Verified
          </Badge>
        );
      case "manual_override":
        return (
          <Badge variant="secondary">
            <Edit2 className="h-3 w-3 mr-1" />
            Manual
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <XCircle className="h-3 w-3 mr-1" />
            Unverified
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Catalog</h1>
          <p className="text-muted-foreground mt-1">
            Master catalog of all funds in your organization
          </p>
        </div>
        <div className="flex gap-2">
          {duplicateCount > 0 && (
            <Button
              variant="outline"
              onClick={() => setDuplicateResolverOpen(true)}
            >
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              {duplicateCount} Potential Duplicate
              {duplicateCount > 1 ? "s" : ""}
            </Button>
          )}
          <Button onClick={() => setAddFundOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Fund
          </Button>
        </div>
      </div>

      {isMFO && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-700">
              <Building2 className="h-4 w-4 inline mr-2" />
              <strong>MFO Mode:</strong> This master catalog is shared across
              all families. When families upload documents, funds are
              automatically matched to prevent duplicates.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Funds</CardTitle>
              <CardDescription>
                {funds.length} fund{funds.length !== 1 ? "s" : ""} in catalog
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search funds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[300px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fund Name</TableHead>
                <TableHead>GP / Issuer</TableHead>
                <TableHead>Vintage</TableHead>
                <TableHead>Identifiers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Positions</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredFunds.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {searchQuery
                      ? "No funds match your search"
                      : "No funds in catalog yet"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFunds.map((fund) => (
                  <TableRow key={fund.id}>
                    <TableCell className="font-medium">{fund.name}</TableCell>
                    <TableCell>{fund.issuer || "-"}</TableCell>
                    <TableCell>{fund.vintage_year || "-"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {fund.sec_cik && (
                          <a
                            href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${fund.sec_cik}&type=&dateb=&owner=include&count=40`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            CIK: {fund.sec_cik}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {fund.lei && (
                          <a
                            href={`https://search.gleif.org/#/record/${fund.lei}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            LEI: {fund.lei.substring(0, 8)}...
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {!fund.sec_cik && !fund.lei && (
                          <span className="text-xs text-muted-foreground">
                            None
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(fund.identifier_status)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{fund._positionCount}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleVerifyIdentifiers(fund)}
                            disabled={fund.identifier_status === "verified"}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Mark as Verified
                          </DropdownMenuItem>
                          {fund.sec_cik && (
                            <DropdownMenuItem
                              onClick={() =>
                                window.open(
                                  `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${fund.sec_cik}`,
                                  "_blank"
                                )
                              }
                            >
                              <ExternalLink className="h-4 w-4" />
                              View on SEC EDGAR
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeleteFund(fund)}
                            className="text-destructive"
                            disabled={
                              (fund._positionCount ?? 0) > 0
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Fund
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FundLookupDialog
        open={addFundOpen}
        onOpenChange={setAddFundOpen}
        onFundSelected={(id, name) => {
          toast.success(`Selected fund: ${name}`);
          fetchFunds();
        }}
      />

      <DuplicateResolver
        open={duplicateResolverOpen}
        onOpenChange={setDuplicateResolverOpen}
        onResolved={() => {
          fetchFunds();
          checkForDuplicates();
        }}
      />
    </div>
  );
}
